// TypeScript axios wrapper
import axios from 'axios'
import qs from 'qs'
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { RequestConfig, RequestInterceptors } from './types'
import { progress } from '@/components/progressBar/progressBar'

const SESSION_AUTH_ERROR_CODES = new Set<number>([
	-60,
	-7,
	-5,
]);

const LEGACY_SESSION_AUTH_ERROR_CODES = new Set<number>([
	-60,
	-7,
]);

// Login page is now hosted in this app at /signin
const AUTH_URL = '/signin';

// API base: backend origin + /api when VITE_API_BASE_URL is set (e.g. Cloudflare → VPS), else /api for dev proxy
const base = import.meta.env.VITE_API_BASE_URL;
export const apiBaseUrl = base ? `${String(base).replace(/\/$/, '')}/api` : '/api';

function isSessionAuthError(response: AxiosResponse | undefined): boolean {
	if (!response) return false;
	const code = Number(response.data?.code);
	if (response.status === 401) return SESSION_AUTH_ERROR_CODES.has(code);
	return LEGACY_SESSION_AUTH_ERROR_CODES.has(code);
}

class Request {
	instance: AxiosInstance
	interceptorsObj?: RequestInterceptors

	constructor(config: RequestConfig) {
		this.instance = axios.create(config);
		this.interceptorsObj = config.interceptors

		this.instance.interceptors.request.use(
			(res: InternalAxiosRequestConfig) => {
				// Start progress bar on request
				progress.start();
				return res;
			},
			(err: any) => {
				progress.done();
				return err;
			},
		)

		this.instance.interceptors.request.use(
			this.interceptorsObj?.requestInterceptors,
		)
		this.instance.interceptors.response.use(
			this.interceptorsObj?.responseInterceptors,
			this.interceptorsObj?.responseInterceptorsCatch,
		)

		this.instance.interceptors.response.use(
			(res: AxiosResponse) => {
				// Complete progress bar on response
				progress.done();
				// Handle session expiration or invalid token (code -7 is TOKEN_ERROR)
				// Only redirect if we have a valid response with TOKEN_ERROR code
				if(isSessionAuthError(res)) {
					// Clear user info from localStorage
					localStorage.removeItem('userInfo');
					// Dispatch custom event to notify other components
					window.dispatchEvent(new Event('userInfoChanged'));
					// Redirect to login page only if not already on a public page
					const currentPath = location.pathname;
					if (!['/', '/signin', '/signup', '/reset', '/fine-tuning', '/pricing', '/terms'].includes(currentPath)) {
						location.href = AUTH_URL;
					}
				}
				// Return res.data directly so cloudRequest gets the API response body
				return res.data;
			},
			(err: any) => {
				// Complete progress bar on error
				progress.done();
				if (isSessionAuthError(err.response)) {
					localStorage.removeItem('userInfo');
					// Dispatch custom event to notify other components
					window.dispatchEvent(new Event('userInfoChanged'));
					const currentPath = location.pathname;
					if (!['/', '/signin', '/signup', '/reset', '/fine-tuning', '/pricing', '/terms'].includes(currentPath)) {
						location.href = AUTH_URL;
					}
				}
				// For network errors (no response), just reject without redirecting
				return Promise.reject(err);
			},
		)
	}
	request<T>(config: RequestConfig): Promise<T> {
		return new Promise((resolve, reject) => {
			if(config.interceptors?.requestInterceptors) {
				config = config.interceptors.requestInterceptors(config);
			}
			this.instance.request<any, T>(config).then(res =>{
				if(config.interceptors?.responseInterceptors) {
					res = config.interceptors.responseInterceptors<T>(res);
				}
				resolve(res);
			}).catch((err: any) => {
				reject(err);
			})
		})
	}
}
// console.log(import.meta.env.DEV)
// console.log(import.meta.env.PROD)
const request = new Request({
	baseURL: apiBaseUrl,
	timeout: 30000,
	withCredentials: true,
	interceptors: {
		// Instance request interceptor
		requestInterceptors: config => {
			return config;
		},
		// Instance response interceptor
		responseInterceptors: result => {
			return result;
		}
	}
})
export default request;